import React, { useState, useRef, useMemo, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { getProviderList, getMasterForProviderList } from '../../../Services/ProviderServices';
import { AxiosResponse } from 'axios';
import { utils } from '../../../helpers/utils';
import AppConfig from '../../../Constants/config';
import { ProviderFormMasterData } from './useProviderForm';

export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}

export interface ColumnDefinition {
    columnName: string;
    sortable: boolean;
    columnHeader: string;
    decorator?: Function;
}

export interface GetProviderListPayload {
    sortColumnName: string | null;
    isDesc: boolean;
    searchText: string | null;
    pageSize: number;
    currentPage: number;
    providerCategoryId?: number;
    providerTypeId?:number;
    providerCredentialIds?:number[];
    providerTrainingIds?:number[];
}

export interface GetProviderListDataRow {
    providerId: number;
    providerCategoryId: number;
    providerName: string;
    providerType: string;
    providerCredential: string;
    trainingOffered: string;
    contact?: string;
    email?: string;
    phone?: string;
    websiteURL?: string;
    address? : string;
    [key: string]: any;
}

export interface GetProviderListData {
    totalRecords: number;
    dataTableData: GetProviderListDataRow[];
}

export interface MasterDataItem {
    displayName: string;
    iconName?:string;
    id:number;
}

export interface MasterData {
    providerCategorys: PageSizeOption[],
    providerCredentials: PageSizeOption[],
    providerTranings: PageSizeOption[],
    providerTypes: PageSizeOption[],
}

export interface MasterDataResponse {
    providerCategorys: MasterDataItem[],
    providerCredentials: MasterDataItem[],
    providerTranings: MasterDataItem[],
    providerTypes: MasterDataItem[],
}

export function useProvidersList(initialProviderCategoryId:number = 0 ,saveState:boolean = true, statePropName?:string) {
    const columns: ColumnDefinition[] = [
        {
            columnName: 'providerName',
            sortable: false,
            columnHeader: 'Provider',
            decorator: (row: GetProviderListDataRow, data: GetProviderListDataRow[], rowIndex: number, colIndex: number) => {
                return (<Link to={`/EditProvider/${row.providerId}`}>{row.providerName}</Link>);
            },
        },
        {
            columnName: 'providerCategoryId',
            sortable: false,
            columnHeader: 'Provider Category',
            decorator: (row: GetProviderListDataRow, data: GetProviderListDataRow[], rowIndex: number, colIndex: number) => {
                if(masterData){
                    return (<span>{categoryIdMap.get(row.providerCategoryId) || ''}</span>);
                }else {
                    return "";
                }
            },
        },
        {
            columnName: 'providerType',
            sortable: false,
            columnHeader: 'Provider Type',
            decorator: undefined,
        },
        {
            columnName: 'providerCredential',
            sortable: false,
            columnHeader: 'Credential',
            decorator: undefined,
        },
        {
            columnName: 'trainingOffered',
            sortable: false,
            columnHeader: 'Training Offered',
            decorator: undefined,
        },
    ];
    const pageSizeOptions = useMemo<PageSizeOption[]>(() => {
        return [
            { key: 3, value: 3, text: '3' },
            { key: 5, value: 5, text: '5' },
            { key: 10, value: 10, text: '10' },
            { key: 15, value: 15, text: '15' },
            { key: 20, value: 20, text: '20' },
            { key: 50, value: 50, text: '50' },
            { key: 100, value: 100, text: '100' }
        ];
    }, []);

    const searchRef = useRef<HTMLInputElement>(null);
    const [masterData, setMasterData] = useState<MasterData | null>(null);
    const [providerCategoryId, setProviderCategoryId] = useState<number>(0);
    const [providerCredentialIds, setProviderCredentialIds] = useState<number[]>([]);
    const [providerTraningsIds, setProviderTraningIds] = useState<number[]>([]);
    const [providerTypeId, setProviderTypeId] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [sortColumnName, setSortColumnName] = useState<string | null>(null);
    const [isDesc, setIsDesc] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [data, setData] = useState<GetProviderListDataRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [categoryIdMap, setCategoryIdMap] = useState<Map<number, string>>(new Map<number, string>());

    const providerCategoryText = getProviderCategoryText(providerCategoryId, masterData? masterData.providerCategorys : []);

    function getProviderCategoryText(providerCategoryId:number, providerCategoriesMaster: PageSizeOption[] ) {
        let result = '';
        providerCategoriesMaster.forEach((item)=>{
            if(item.value === providerCategoryId){
                result = item.text;
            }
        });
        return result;
    }

    const handlePageSizeChange: ((event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void) | undefined = (event, data) => {
        setPageSize(data.value as number);
        const payload: GetProviderListPayload = {
            pageSize: data.value as number,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: currentPage,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }

    const handlePaginationChange: (newPageNumber: number) => void = (newPageNumber) => {
        setCurrentPage(newPageNumber);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: newPageNumber,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }

    const handleProviderCategoryChange: (newProviderCategoryId: number) => void = (newProviderCategoryId) => {
        setProviderCategoryId(newProviderCategoryId);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: 1,
            providerCategoryId: newProviderCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }

    const handleProviderCredentialsChange: (newProviderCredentialIds: number[]) => void = (newProviderCredentialIds) => {
        setProviderCredentialIds(newProviderCredentialIds);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: 1,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: newProviderCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }

    const handleProviderTrainingsChange: (newProviderTraingIds: number[]) => void = (newProviderTraingIds) => {
        setProviderTraningIds(newProviderTraingIds);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: 1,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: newProviderTraingIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }

    const handleProviderTypeChange: (newProviderTypeId: number) => void = (newProviderTypeId) => {
        setProviderTypeId(newProviderTypeId);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: 1,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: newProviderTypeId,
        };
        _fetchData(payload);
    }

    const handleSortChange: (newSortColumnName: string) => void = (newSortColumnName) => {
        let newIsDesc = isDesc;
        let newColumnName = sortColumnName;
        if (newSortColumnName === sortColumnName) {
            newIsDesc = !newIsDesc;
        } else {
            newColumnName = newSortColumnName;
        }

        setIsDesc(newIsDesc);
        setSortColumnName(newColumnName);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: newColumnName,
            isDesc: newIsDesc,
            searchText: searchText,
            currentPage: 1,
        };
        _fetchData(payload);
    }

    const handleSearchChangeFn: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        //setSearchText((event.target as HTMLInputElement).value);
        setSearchText((event.target as HTMLInputElement).value);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: (event.target as HTMLInputElement).value,
            currentPage: 1,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }
    const handleSearchChange = _.debounce(handleSearchChangeFn, 1000);

    const resetSearch: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (searchRef.current !== null) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setCurrentPage(1);
        const payload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: null,
            currentPage: 1,
            providerCategoryId: providerCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        _fetchData(payload);
    }

    const _fetchData: (overwritePayload?: GetProviderListPayload) => void = (overwritePayload) => {
        const defaultPayload: GetProviderListPayload = {
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            isDesc: isDesc,
            searchText: searchText,
            currentPage: currentPage,
            providerCategoryId: initialProviderCategoryId,
            providerCredentialIds: providerCredentialIds,
            providerTrainingIds: providerTraningsIds,
            providerTypeId: providerTypeId,
        };
        let payload: GetProviderListPayload = defaultPayload;
        if (overwritePayload !== undefined) {
            payload = overwritePayload;
        }
        //console.log(payload);
        setIsLoading(true);
        if(saveState){
            _saveState(payload);
        }
        getProviderList(payload, false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, GetProviderListData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {
                let data = response.data as GetProviderListData;
                setData(data.dataTableData);
                setTotalRecords(data.totalRecords);
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        setIsLoading(true);
        getMasterForProviderList(false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, MasterDataResponse> = result as AxiosResponse;
            if (response && response.status === 200 && response.data) {
                let data = response.data as MasterDataResponse;
                let newMasterData:MasterData = {
                    providerCategorys: [],
                    providerCredentials: [],
                    providerTranings: [],
                    providerTypes: []
                };
                
                let providerCategoriesOptions: PageSizeOption[] = data.providerCategorys.map((item,index)=>{
                    categoryIdMap.set(item.id, item.displayName);
                    return {
                        key: index,
                        value: item.id,
                        text: item.displayName
                    }
                });
                providerCategoriesOptions.unshift({key: -1,value:0,text:'All'});
                
                let providerCredentialsOptions: PageSizeOption[] = data.providerCredentials.map((item,index)=>{
                    return {
                        key: index,
                        value: item.id,
                        text: item.displayName
                    }
                });
                
                let providerTrainingOptions: PageSizeOption[] = data.providerTranings.map((item,index)=>{
                    return {
                        key: index,
                        value: item.id,
                        text: item.displayName
                    }
                });
                
                let providerTypesOptions: PageSizeOption[] = data.providerTypes.map((item,index)=>{
                    return {
                        key: index,
                        value: item.id,
                        text: item.displayName
                    }
                });
                providerTypesOptions.unshift({key: -1,value:0,text:'All'});

                newMasterData.providerCategorys= providerCategoriesOptions;
                newMasterData.providerCredentials= providerCredentialsOptions;
                newMasterData.providerTranings= providerTrainingOptions;
                newMasterData.providerTypes= providerTypesOptions;
                setMasterData(newMasterData);

            }
            callBack();
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _saveState = (currentPayload: GetProviderListPayload) => {
        let stateName = statePropName || AppConfig.PROVIDER_LIST_STATE;
        if(saveState){
            utils.updateInLocalStorage(stateName, JSON.stringify(currentPayload));
        }
    }

    const _loadState = () => {
        let stateName = statePropName || AppConfig.PROVIDER_LIST_STATE;
        let stateString: string | null = utils.getLocalStorageItem(stateName);
        if (stateString && saveState) {
            return JSON.parse(stateString) as GetProviderListPayload;
        } else {
            return null;
        }

    }

    useEffect(() => {
        _fetchMasterData(() => {
            let priviousState: GetProviderListPayload | null = _loadState();
            
            if(initialProviderCategoryId !== 0){
                setProviderCategoryId(initialProviderCategoryId);
            }

            if (priviousState && saveState) {
                _fetchData(priviousState);
                setSortColumnName(priviousState.sortColumnName);
                setIsDesc(priviousState.isDesc);
                setSearchText(priviousState.searchText);
                setPageSize(priviousState.pageSize);
                setCurrentPage(priviousState.currentPage);
                setProviderCategoryId(priviousState.providerCategoryId || 0);
                setProviderCredentialIds(priviousState.providerCredentialIds || []);
                setProviderTraningIds(priviousState.providerTrainingIds || []);
                setProviderTypeId(priviousState.providerTypeId || 0);
                if (searchRef.current) {
                    searchRef.current.value = priviousState.searchText || '';
                }
            } else {
                _fetchData();
            }
        });

    }, []);

    return [pageSize, totalRecords, currentPage,
        pageSizeOptions, searchRef, searchText, columns, data, isLoading, isDesc, sortColumnName,
        masterData,providerCategoryId,providerCategoryText,providerCredentialIds, providerTraningsIds, providerTypeId,
        handlePageSizeChange, handleSearchChange, resetSearch, handlePaginationChange, handleSortChange,
        handleProviderCategoryChange, handleProviderCredentialsChange, handleProviderTrainingsChange, handleProviderTypeChange] as const;
}